import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { PopoverController } from '@ionic/angular/standalone';
import { ItemInputComponent } from 'src/app/components/item-input/item-input.component';
import { ItemLabelComponent } from 'src/app/components/item-label/item-label.component';
import { ItemValueComponent } from 'src/app/components/item-value/item-value.component';
import { ItemComponent } from 'src/app/components/item/item.component';
import { Unit, UnitPopoverComponent } from '../shared/popovers/unit-popover/unit.popover';

export interface UnitValue {
	unit: string | null;
	value: string | null;
}

@Component({
    selector: 'app-unit-value',
    templateUrl: './unit-value.control.html',
    styleUrl: './unit-value.control.scss',
    imports: [ReactiveFormsModule, ItemComponent, ItemLabelComponent, ItemInputComponent, ItemValueComponent],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [{
            provide: NG_VALUE_ACCESSOR,
            useExisting: UnitValueControl,
            multi: true
        }]
})
export class UnitValueControl implements ControlValueAccessor, OnInit {

	@Input() readOnly: boolean = false;
	@Input() label: string = '';
	@Input() unitLabel: string = '';
	@Input() units: Unit[] = [];
	@Input() placeholder: string = '';
	@Input() maxlength: number | null = null;

	@ViewChild(ItemInputComponent) itemInputComponent!: ItemInputComponent;

	private unitValue!: UnitValue;

	get unit() {
		if (this.unitValue.unit === null) return '...';
		return this.units.length ? this.units.find(u => u.value === this.unitValue.unit || u.label === this.unitValue.unit)?.label ?? '???' : this.unitLabel;
	}
	get value() { return this.unitValue.value ?? ''; }

	constructor(private changeDetectorRef: ChangeDetectorRef, private popoverController: PopoverController) {
	}

	ngOnInit(): void {
		if (this.unitLabel && this.units.length > 0) throw new Error('Cannot specify both unitLabel and units. Only one of the inputs should be specified');
		this.unitValue = this.getDefaultValue();
	}

	writeValue(value: UnitValue | null): void {
		this.unitValue = structuredClone(value) ?? this.getDefaultValue();

		// this is required for the view to update correctly (it's a general approach recommended by Angular devs using ChangeDetectionStrategy.OnPush: https://github.com/angular/angular/issues/21780)
		this.changeDetectorRef.markForCheck();
	}

	private getDefaultValue() {
		return { unit: this.unitLabel || null, value: null } as UnitValue;
	}

	private onChange: (value: UnitValue) => void = () => { };

	registerOnChange(fn: (value: UnitValue) => void): void {
		this.onChange = fn;
	}

	private onTouched: () => void = () => { };

	registerOnTouched(fn: () => void): void {
		this.onTouched = fn;
	}

	setDisabledState(disabled: boolean): void {
	}

	valueChanged(value: string | null) {

		this.onTouched();

		if (value === null || value === '') {
			this.unitValue.value = null;
		}
		else this.unitValue.value = value;
		this.onChange(structuredClone(this.unitValue));
	}

	async presentUnitsPopover(event: Event) {
		if (this.readOnly) return;

		this.onTouched();

		const popover = await this.popoverController.create({
			component: UnitPopoverComponent,
			cssClass: 'popover-menu',
			componentProps: { units: this.units },
			event: event,
			alignment: 'end',
			showBackdrop: false
		});

		await popover.present();

		const { data } = await popover.onWillDismiss();

		if (!data) return;

		const unit = data.unit as Unit;

		if (this.unitValue.unit === (unit.value ?? unit.label)) return;

		this.unitValue.unit = unit.value ?? unit.label;

		this.onChange(structuredClone(this.unitValue));
		this.changeDetectorRef.markForCheck();
	}

	itemFocused() {
		this.onTouched();
	}

	controlClicked() {
		if (!this.readOnly) this.itemInputComponent.focusInput();
	}
}